import { useEffect, useState, type ReactElement } from 'react';
import { type VorlagenArt, type VorlagenDaten } from '@schnitt/shared';
import { api, type VorlageView } from '../api';
import { useEditor } from '../store';
import { Sheet } from './Sheet';

/**
 * Vorlagen (S24). Sascha: „Einmal einstellen, immer wieder benutzen.“
 *
 * Ein Blatt für beide Sorten: oben sichern, was gerade eingestellt ist, unten
 * die Liste zum Anwenden. Zwei Blätter, die sich nur im Wort unterscheiden,
 * wären zwei Stellen, an denen derselbe Fehler zweimal steckt.
 */
export function VorlagenSheet({
  art,
  aktuell,
  anwenden,
  onClose,
}: {
  art: VorlagenArt;
  /** Was gerade eingestellt ist -- das, was ein „Sichern“ mitnimmt. */
  aktuell: VorlagenDaten;
  anwenden: (daten: VorlagenDaten) => void;
  onClose: () => void;
}): ReactElement {
  const toast = useEditor((s) => s.toast);
  const role = useEditor((s) => s.role);
  const [liste, setListe] = useState<VorlageView[]>([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const laden = async () => {
    try {
      setListe((await api.vorlagen(art)).vorlagen);
    } catch {
      toast('error', 'Die Vorlagen liessen sich nicht holen.');
    }
  };
  useEffect(() => {
    void laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [art]);

  const sichern = async () => {
    const wie = name.trim();
    if (!wie) return;
    setBusy(true);
    try {
      const { ersetzt } = await api.vorlageSichern(wie, aktuell);
      setName('');
      await laden();
      toast('success', ersetzt ? `„${wie}“ überschrieben.` : `„${wie}“ gesichert.`);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Das Sichern ging nicht.');
    } finally {
      setBusy(false);
    }
  };

  const loeschen = async (v: VorlageView) => {
    setBusy(true);
    try {
      await api.vorlageLoeschen(v.id);
      await laden();
      toast('info', `„${v.name}“ gelöscht.`);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Löschen ging nicht.');
    } finally {
      setBusy(false);
    }
  };

  const titel = art === 'filter' ? 'Filter-Vorlagen' : 'Export-Vorlagen';

  return (
    <Sheet title={titel} onClose={onClose}>
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 8px' }}>Was jetzt eingestellt ist, sichern</h3>
      <div className="row">
        <input
          className="grow"
          placeholder={art === 'filter' ? 'z. B. Doku-Look' : 'z. B. Reel hochkant'}
          value={name}
          data-vorlage-name
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void sichern();
          }}
          disabled={role === 'viewer'}
        />
        <button
          className="small primary"
          data-vorlage-sichern
          onClick={() => void sichern()}
          disabled={busy || role === 'viewer' || name.trim() === ''}
        >
          Sichern
        </button>
      </div>
      <p className="hint">
        {art === 'filter'
          ? 'Look, Farbkorrektur, Farbräder und die Werkzeuge für den Ton — nicht die Zeiten und nicht die Blenden.'
          : 'Bildformat, Bildrate und Ziellautheit.'}
      </p>

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Gesichert</h3>
      <div className="list" data-vorlagen={String(liste.length)}>
        {liste.length === 0 ? <p className="hint">Noch keine.</p> : null}
        {liste.map((v) => (
          <div className="row" key={v.id} data-vorlage={v.name}>
            <div className="grow">
              <div className="title">{v.name}</div>
              <div className="sub">{v.satz}</div>
            </div>
            <button
              className="small primary"
              data-vorlage-anwenden={v.name}
              disabled={role === 'viewer' || v.daten === null}
              onClick={() => {
                if (!v.daten) return;
                anwenden(v.daten);
                toast('success', `„${v.name}“ angewendet.`);
                onClose();
              }}
            >
              Anwenden
            </button>
            <button className="small" disabled={busy || role === 'viewer'} onClick={() => void loeschen(v)}>
              Löschen
            </button>
          </div>
        ))}
      </div>
      {art === 'filter' ? (
        <p className="hint">
          Anwenden <em>ersetzt</em>, was am Clip eingestellt ist — zweimal dasselbe ergibt dasselbe.
        </p>
      ) : null}
    </Sheet>
  );
}
